/**
 * WPML browser-language prompt behavior.
 *
 * Reads static page config injected by PHP, evaluates cookie + browser language
 * client-side. The optional `browser-lang-notice` modal runs only when the page
 * language and browser-preferred language match `promptPageLang` + `promptBrowserLang`
 * (defaults: English page + Spanish browser). Any other mismatch sets `grow_lang`
 * to the current page language without opening the modal.
 */
(function () {
	/**
	 * @param {string} targetUrl - Navigation target (relative or absolute).
	 * @return {string} URL with current address bar query merged in.
	 */
	function mergeNavUrl(targetUrl) {
		if (
			typeof window.growtherapyWpmlMergeLocationSearchIntoUrl === 'function'
		) {
			return window.growtherapyWpmlMergeLocationSearchIntoUrl(targetUrl);
		}
		return targetUrl;
	}

	/**
	 * Check whether the configured language cookie already exists.
	 *
	 * @param {string} cookieName - Cookie key to check.
	 * @return {boolean} True when cookie is already present.
	 */
	function hasLanguageCookie(cookieName) {
		if (
			!cookieName ||
			typeof document.cookie !== 'string' ||
			!document.cookie
		) {
			return false;
		}

		const parts = document.cookie.split(';');
		for (let i = 0; i < parts.length; i++) {
			const part = parts[i].trim();
			if (part.indexOf(cookieName + '=') === 0) {
				return true;
			}
		}

		return false;
	}

	/**
	 * Read the configured language cookie value.
	 *
	 * @param {string} cookieName - Cookie key to read.
	 * @return {string} Cookie value, or empty string when missing.
	 */
	function getLanguageCookieValue(cookieName) {
		if (
			!cookieName ||
			typeof document.cookie !== 'string' ||
			!document.cookie
		) {
			return '';
		}

		const parts = document.cookie.split(';');
		for (let i = 0; i < parts.length; i++) {
			const part = parts[i].trim();
			if (part.indexOf(cookieName + '=') === 0) {
				const rawValue = part.slice(cookieName.length + 1);
				return rawValue ? decodeURIComponent(rawValue) : '';
			}
		}

		return '';
	}

	function setLangCookie(cfg, lang) {
		if (typeof window.growtherapyWpmlWriteLanguageCookie !== 'function') {
			return;
		}
		window.growtherapyWpmlWriteLanguageCookie(cfg, lang);
	}

	/**
	 * Resolve the first supported browser language (base code).
	 *
	 * @param {Object} cfg - Browser prompt runtime config.
	 * @return {string} Matching language code or empty string.
	 */
	function getPreferredLang(cfg) {
		let browserLangs = [];
		if (
			typeof navigator !== 'undefined' &&
			Array.isArray(navigator.languages) &&
			navigator.languages.length
		) {
			browserLangs = navigator.languages;
		} else if (typeof navigator !== 'undefined' && navigator.language) {
			browserLangs = [navigator.language];
		}

		const supported = Array.isArray(cfg.supportedLangs)
			? cfg.supportedLangs
			: [];

		for (let i = 0; i < browserLangs.length; i++) {
			const raw = browserLangs[i];
			const code = raw && raw.split ? raw.split('-')[0].toLowerCase() : '';
			if (code && supported.indexOf(code) >= 0) {
				return code;
			}
		}

		return '';
	}

	/**
	 * Whether the browser-lang modal should run for this visit.
	 *
	 * @param {Object} cfg           - Browser prompt runtime config.
	 * @param {string} preferredLang - First supported browser language code.
	 * @param {string} switchPath    - Relative URL for preferred language (if any).
	 * @return {boolean} True when modal flow should run.
	 */
	function shouldShowBrowserPrompt(cfg, preferredLang, switchPath) {
		if (!preferredLang || preferredLang === cfg.currentLang || !switchPath) {
			return false;
		}

		const pageLang =
			typeof cfg.promptPageLang === 'string' && cfg.promptPageLang
				? cfg.promptPageLang
				: 'en';
		const browserLang =
			typeof cfg.promptBrowserLang === 'string' && cfg.promptBrowserLang
				? cfg.promptBrowserLang
				: 'es';

		return cfg.currentLang === pageLang && preferredLang === browserLang;
	}

	/**
	 * Open and wire browser-lang-notice modal interactions.
	 *
	 * @param {Object} cfg - Browser prompt runtime config.
	 * @return {void}
	 */
	function runPrompt(cfg) {
		const modalId = cfg.modalId || 'browser-lang-notice';
		let promptResolved = false;
		let attempts = 0;
		const maxAttempts = 30;

		function bindActions() {
			document.addEventListener('click', function (event) {
				const target = event.target;
				if (!target || !target.closest) {
					return;
				}

				const stayButton = target.closest('[data-wpml-browser-lang-stay]');
				if (stayButton) {
					event.preventDefault();
					promptResolved = true;
					if (window.SegmentTracking?.trackButtonClick) {
						window.SegmentTracking.trackButtonClick(
							`Browser Lang Notice Modal [Keep English] - Click`,
							{
								component_name: 'Browser Lang Notice Modal',
								current_language: cfg.currentLang || '',
								stay_language: cfg.stayLang || '',
							},
						);
					}
					setLangCookie(cfg, cfg.stayLang);
					const modalManager = window.growtherapyResponsiveModalManager;
					if (
						modalManager &&
						typeof modalManager.closeActiveModal === 'function'
					) {
						modalManager.closeActiveModal();
					}
					return;
				}

				const switchButton = target.closest('[data-wpml-browser-lang-switch]');
				if (switchButton) {
					event.preventDefault();
					promptResolved = true;
					if (window.SegmentTracking?.trackButtonClick) {
						window.SegmentTracking.trackButtonClick(
							`Browser Lang Notice Modal [Switch to Spanish] - Click`,
							{
								component_name: 'Browser Lang Notice Modal',
								current_language: cfg.currentLang || '',
								preferred_language: cfg.preferredLang || '',
							},
						);
					}
					if (cfg.switchPath && cfg.preferredLang) {
						setLangCookie(cfg, cfg.preferredLang);
						window.location.href = mergeNavUrl(cfg.switchPath);
					}
				}
			});

			document.addEventListener(
				'growtherapyResponsiveModalClosed',
				function (event) {
					if (!event.detail || event.detail.id !== modalId || promptResolved) {
						return;
					}

					promptResolved = true;
					if (!hasLanguageCookie(cfg.cookieName)) {
						setLangCookie(cfg, 'en');
					}
				},
			);
		}

		function tryOpenModal() {
			attempts += 1;
			const modalManager = window.growtherapyResponsiveModalManager;
			if (
				modalManager &&
				typeof modalManager.openById === 'function' &&
				modalManager.openById(modalId)
			) {
				return;
			}

			if (attempts < maxAttempts) {
				setTimeout(tryOpenModal, 100);
			}
		}

		bindActions();
		if (window.growtherapyResponsiveModalManager) {
			tryOpenModal();
		} else {
			document.addEventListener(
				'growtherapyResponsiveModalReady',
				tryOpenModal,
			);
		}

		setTimeout(tryOpenModal, 50);
	}

	/**
	 * Main runtime entrypoint for browser-language prompt logic.
	 *
	 * @return {void}
	 */
	function run() {
		const cfg = window.growtherapyWpmlBrowserLang;
		if (!cfg || !cfg.cookieName || !cfg.currentLang) {
			return;
		}

		const cookieLang = getLanguageCookieValue(cfg.cookieName);
		if (cookieLang) {
			const cookieRedirectPath =
				cfg.redirectUrls && cfg.redirectUrls[cookieLang]
					? cfg.redirectUrls[cookieLang]
					: '';

			if (cookieLang !== cfg.currentLang && cookieRedirectPath) {
				const currentUrl = new URL(
					window.location.pathname +
						window.location.search +
						window.location.hash,
					window.location.origin,
				);
				const targetUrl = new URL(cookieRedirectPath, window.location.origin);

				const mergedHref = mergeNavUrl(targetUrl.href);
				if (currentUrl.href !== mergedHref) {
					window.location.href = mergedHref;
					return;
				}
			}

			return;
		}

		const preferredLang = getPreferredLang(cfg);
		const switchPath =
			cfg.redirectUrls && preferredLang ? cfg.redirectUrls[preferredLang] : '';

		if (shouldShowBrowserPrompt(cfg, preferredLang, switchPath)) {
			cfg.preferredLang = preferredLang;
			cfg.switchPath = switchPath;
			cfg.stayLang = cfg.currentLang;
			runPrompt(cfg);
			return;
		}

		setLangCookie(cfg, cfg.currentLang);
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', run);
	} else {
		run();
	}
})();
